/**
 * فهرستِ بازی‌ها — یک منبع، دو مصرف.
 *
 * `catalog` همه‌ی بازی‌های بازی وان است و صفحه‌های سایت (`site/build.ts`) از روی
 * همین ساخته می‌شوند. `games` فقط آن‌هایی است که سرور واقعاً می‌شناسد و کلاینت
 * می‌تواند بازی‌شان کند؛ شناسه‌هایش باید دقیقاً همانی باشد که سرور دارد
 * (Backend/internal/match/match.go → Games). یک‌بار «charbarg» فرستادیم و سرور
 * «unsupported_game» داد؛ آزمونِ کنارِ همین فایل نگهش می‌دارد.
 *
 * `online: false` یعنی بازی در اپِ اندروید هست ولی هنوز میزِ آنلاین ندارد —
 * صفحه‌اش ساخته می‌شود، ولی دکمه‌اش به کافه‌بازار می‌رود نه به میز.
 * `rooms: true` یعنی آنلاین هست ولی فقط در اتاقِ دوستان (تخته‌ای‌ها).
 */
export type Pip = "spade" | "heart" | "diamond" | "club" | "joker" | "star" | "dice" | "mask" | "arrow";

/** دسته‌ی بازی — قفسه‌های سایت و تالارِ اپ همین را دارند (GameCatalog.Categories). puzzle = «فکری». */
export type Cat = "card" | "board" | "party" | "puzzle";

export type Game = {
  /** شناسه‌ی سرور؛ نامِ فایلِ صفحه‌ی بازی هم همین است. */
  id: string;
  fa: string;
  en: string;
  /** تعدادِ نفرات — زیرِ نامِ بازی می‌نشیند. */
  note: string;
  /** هدفِ بازی در یک جمله. */
  blurb: string;
  color: string;
  pip: Pip;
  online: boolean;
  /**
   * آنلاین فقط در اتاقِ دوستان — بی صفِ «بازیِ سریع» و بی ورودیِ سکه. تخته‌ای‌ها
   * (Backend/internal/match/match.go → BoardGames): صفِ سرور این‌ها را با
   * `rooms_only` رد می‌کند و ورودیِ اتاقشان همیشه صفر است.
   */
  rooms?: boolean;
  /** دسته؛ نبود یعنی ورق. */
  cat?: Cat;
  /** «چطور بازی کنیم» — سه تا پنج بند. */
  rules: string[];
};

export const catalog: Game[] = [
  {
    id: "hokm",
    fa: "حکم",
    en: "Hokm",
    note: "۲ تا ۶ نفره",
    blurb: "حکم بزن و دست‌ها را ببر",
    color: "linear-gradient(180deg,#5b9aa9,#3c6f80)",
    pip: "spade",
    online: true,
    rules: [
      "چهار نفر، دو تیم روبه‌رو؛ به هر کس سیزده کارت می‌رسد.",
      "اولین کسی که آس بگیرد حاکم می‌شود: پنج کارت اولش را می‌بیند و خال برتر یا «حکم» را اعلام می‌کند.",
      "اگر از خال زمین داری، باید همان را بریزی. اگر نداری، هر کارتی می‌توانی بریزی؛ حتی حکم، برای بُریدن.",
      "بالاترین کارت حکم دست را می‌بَرد، و اگر حکمی نیفتاده باشد، بالاترین کارت خال زمین.",
      "هر تیمی که زودتر هفت دست بگیرد، آن دور را با یک امتیاز می‌بَرد؛ هفت بر صفر «کُت» است و دو امتیاز دارد. اولین تیمی که به هفت امتیاز برسد، مسابقه را می‌بَرد.",
    ],
  },
  {
    id: "chahar",
    fa: "چهاربرگ",
    en: "Chahar Barg",
    note: "۲ تا ۴ نفره",
    blurb: "با مجموع یازده بردار",
    color: "linear-gradient(180deg,#7d5bb0,#553b80)",
    pip: "diamond",
    online: true,
    rules: [
      "به هر نفر چهار برگ می‌رسد و چهار برگ روی زمین باز است.",
      "کارت عددی فقط وقتی برمی‌دارد که مجموعش با کارت‌های زمین دقیقاً یازده شود. بی‌بی با بی‌بی و شاه با شاه.",
      "سرباز همه‌ی کارت‌های عددی و سربازهای زمین را جارو می‌کند، ولی بی‌بی و شاه را نه.",
      "خالی‌کردن کامل زمین «سور» است و پنج امتیاز دارد.",
      "امتیاز هر دور بیست است: هفت‌خاج ۷، ۱۰ خشت ۳، ۲ گشنیز ۲، هر آس ۱ و هر سرباز ۱. شمار کارت خودش امتیازی ندارد.",
    ],
  },
  {
    id: "haft",
    fa: "هفت خبیث",
    en: "Haft Khabis",
    note: "۲ تا ۶ نفره",
    blurb: "زودتر دستت را خالی کن",
    color: "linear-gradient(180deg,#c8504a,#933531)",
    pip: "heart",
    online: true,
    rules: [
      "کارتی هم‌خال یا هم‌عدد با کارت روی زمین بگذار تا زودتر از همه دستت خالی شود؛ اگر نداشتی، از پشته بکش.",
      "کارت‌های خبیث بازی را برمی‌گردانند: ۷ نفر بعدی را دو برگ جریمه می‌کند و جریمه‌ها روی هم جمع می‌شوند، ۸ یک نوبت اضافه می‌دهد، ۱۰ جهت بازی را برمی‌گرداند، آس نوبت نفر بعد را می‌سوزاند و سرباز خال را عوض می‌کند.",
      "با ۲ «تعارف» می‌کنی: یک حریف ندیده یک برگ از دستت برمی‌دارد. ته دور، ورق‌های مانده امتیاز منفی‌اند.",
      "وقتی یک برگ بیشتر نداری «هشدار» را بزن، وگرنه جریمه می‌شوی.",
    ],
  },
  {
    id: "shelam",
    fa: "شلم",
    en: "Shelem",
    note: "۴ نفره تیمی",
    blurb: "شرط ببند و دست بگیر",
    color: "linear-gradient(180deg,#d9a441,#a97327)",
    pip: "club",
    online: true,
    rules: [
      "چهار نفر، دو تیم روبه‌رو؛ به هر کس دوازده برگ می‌رسد و چهار برگ زمین می‌ماند.",
      "مزایده از صد شروع می‌شود و با مضرب پنج بالا می‌رود. آخرین خواننده حکم را اعلام می‌کند و باید به تعهدش برسد.",
      "ارزش‌ها: هر آس ده، هر ۱۰ ده، هر ۵ پنج، به‌علاوه‌ی پنج برای هر دست و پنج برای زمین؛ روی هم ۱۶۵.",
      "اگر به تعهدت نرسی ولی ۸۵ یا بیشتر بگیری، «کُت» است و به اندازه‌ی تعهدت منفی می‌گیری؛ زیر ۸۵ «یاسا» است و منفی ۱۶۵ می‌گیری.",
      "بردن همه‌ی دست‌ها «شلم» است و ۳۳۰ امتیاز دارد.",
    ],
  },
  {
    id: "rami",
    fa: "رامی",
    en: "Rummy",
    note: "۲ تا ۴ نفره",
    blurb: "دسته و رشته بچین",
    color: "linear-gradient(180deg,#4f9e6a,#347148)",
    pip: "club",
    online: false,
    rules: [
      "به هر کس سیزده برگ می‌رسد.",
      "در نوبتت یک کارت از دسته یا از دورریز بکش و یکی دور بینداز.",
      "کل دستت را به «سری» (هم‌رتبه) و «دنباله» (هم‌خال پشت سر هم) بچین تا اعلام کنی.",
      "جوکر جای هر کارتی می‌نشیند.",
      "کم‌ترین جریمه تا صد امتیاز، برنده است.",
    ],
  },
  {
    id: "basicrami",
    fa: "رامی کلاسیک",
    en: "Classic Rummy",
    note: "۲ تا ۴ نفره",
    blurb: "روی میز بچین و بچسبان",
    color: "linear-gradient(180deg,#7ba050,#55703a)",
    pip: "club",
    online: false,
    rules: [
      "یک دسته‌ی ۵۲ برگی بی‌جوکر: دو نفره ده برگ، سه و چهار نفره هفت برگ.",
      "یک کارت بکش، بعد هر سری و دنباله‌ای که داری روی میز باز کن.",
      "وقتی خودت چیده‌ای روی میز داری، می‌توانی کارتت را به چیده‌های بقیه هم بچسبانی. آخر نوبت یک کارت دور بینداز.",
      "هر کس اول دستش خالی شود دور را می‌برد و به اندازه‌ی کارت‌های مانده در دست بقیه امتیاز می‌گیرد (آس ۱، سرباز و بی‌بی و شاه ۱۰).",
      "اولین نفر تا ۵۰۰ امتیاز، برنده است.",
    ],
  },
  {
    id: "gin",
    fa: "جین رامی",
    en: "Gin Rummy",
    note: "۲ نفره",
    blurb: "با کم‌ترین کارت نچیده ناک بزن",
    color: "linear-gradient(180deg,#4fa0b8,#357285)",
    pip: "diamond",
    online: false,
    rules: [
      "دو نفره؛ به هر کس ده برگ می‌رسد.",
      "یک کارت بکش و یکی دور بینداز؛ کارت‌ها را به سری و دنباله بچین.",
      "کارت‌های نچیده جریمه دارند: آس ۱، سرباز و بی‌بی و شاه ۱۰، بقیه عدد خودشان.",
      "وقتی جمع نچیده‌هایت ده یا کمتر شد، می‌توانی «ناک» بزنی و دست را ببندی؛ اگر صفر باشد، «جین» است و ۲۵ امتیاز اضافه دارد.",
      "اولین نفر تا صد امتیاز، برنده است.",
    ],
  },
  {
    id: "bluff",
    fa: "بلوف",
    en: "Bluff",
    note: "۳ تا ۶ نفره",
    blurb: "بلوف بزن، مچت را نگیرند",
    color: "linear-gradient(180deg,#d97b3c,#a4552a)",
    pip: "joker",
    online: false,
    rules: [
      "کل ۵۲ برگ بین همه پخش می‌شود و هر دور یک رتبه ادعا می‌شود.",
      "در نوبتت یک تا چهار برگ پشت‌رو بگذار و بگو از همان رتبه‌اند؛ راست یا دروغ.",
      "می‌توانی پاس بدهی، یا به آخرین کسی که برگ گذاشته «شک» کنی.",
      "شک درست: بلوف‌زن کل زمین را برمی‌دارد. شک اشتباه: خودت برمی‌داری.",
      "هر کس زودتر دستش را راست‌راستکی خالی کند، برنده است.",
    ],
  },
  {
    id: "sheytan",
    fa: "بازی شیطان",
    en: "Old Maid",
    note: "۳ تا ۶ نفره",
    blurb: "جوکر دستت نماند وگرنه شیطانی!",
    color: "linear-gradient(180deg,#b8508f,#853665)",
    pip: "joker",
    online: false,
    rules: [
      "۵۲ برگ به‌اضافه‌ی یک جوکر. همه پخش می‌شود و هر کس اول جفت‌های هم‌رتبه‌اش را می‌اندازد.",
      "در نوبتت یک برگ ندیده از دست حریف سمت راستت بکش.",
      "اگر جفت شد، هر دو را بینداز و دوباره بکش؛ اگر نشد، نوبتت تمام است.",
      "جوکر با هیچ برگی جفت نمی‌شود؛ آخر بازی در دست هر کس بماند، شیطان است.",
      "اولین کسی که دستش خالی شود، «شاه» است.",
    ],
  },
  {
    id: "bidel",
    fa: "بی‌دل",
    en: "Hearts",
    note: "۴ نفره",
    blurb: "دل و بی‌بی‌پیک نگیر",
    color: "linear-gradient(180deg,#b34a5e,#822f41)",
    pip: "heart",
    online: false,
    rules: [
      "چهار نفر، هر کس برای خودش؛ به هر نفر سیزده برگ می‌رسد.",
      "پیش از هر دست سه کارت رد و بدل می‌شود، و دارنده‌ی ۲ گشنیز دست اول را با همان شروع می‌کند.",
      "هم‌خال زمین بریز. تا کسی دل نریخته، نمی‌شود دل سرداد.",
      "هر دل یک امتیاز منفی دارد و بی‌بی‌پیک سیزده؛ روی هم ۲۶ در هر دست. کم‌ترین امتیاز می‌بَرد.",
      "ولی اگر یک نفر هر ۲۶ را یک‌جا بگیرد «شوت ماه» است: خودش صفر می‌شود و آن سه نفر دیگر ۲۶ می‌گیرند.",
    ],
  },
  {
    id: "salter",
    fa: "سالتر",
    en: "Solitaire",
    note: "۱ نفره",
    blurb: "یک‌نفره: کارت‌ها را بچین",
    color: "linear-gradient(180deg,#5a63b5,#3d4482)",
    pip: "spade",
    online: false,
    rules: [
      "یک‌نفره؛ هفت ستون که در هر کدام فقط کارت رویی باز است.",
      "روی ستون‌ها نزولی و یکی سیاه، یکی قرمز بچین؛ مثلاً شش سیاه روی هفت قرمز.",
      "ستون خالی فقط شاه می‌گیرد.",
      "از دسته کارت بکش؛ دسته که تمام شد، دوباره از اول می‌چرخد.",
      "هر چهار خانه‌ی پایه را از آس تا شاه و هم‌خال بچین تا ببری.",
    ],
  },
  {
    id: "bibi",
    fa: "بی‌بی سلام",
    en: "Bibi Salaam",
    note: "۳ تا ۴ نفره",
    blurb: "کندترین دست، کارت‌ها را برمی‌دارد",
    color: "linear-gradient(180deg,#5c7fb8,#3d5885)",
    pip: "star",
    online: false,
    rules: [
      "کل ۵۲ برگ بین همه پخش می‌شود و هر کس دسته‌اش را پشت‌رو نگه می‌دارد.",
      "نوبتی یک کارت از روی دسته‌ات وسط بینداز. روی کارت‌های عددی فقط تماشا کن.",
      "کارت صورت که رو شد باید فوری واکنش بدهی: آس دست زدن، شاه سلام نظامی، بی‌بی گفتن «بی‌بی سلام!» و سرباز سلام.",
      "کندترین نفر همه‌ی کارت‌های وسط را برمی‌دارد و دسته‌اش بزرگ‌تر می‌شود.",
      "هر کس زودتر دسته‌اش تمام شود، برنده است.",
    ],
  },
  {
    id: "bridge",
    fa: "بریج",
    en: "Bridge",
    note: "۴ نفره تیمی",
    blurb: "قرارداد ببند و دست بگیر",
    color: "linear-gradient(180deg,#a97b4f,#7b5637)",
    pip: "spade",
    online: false,
    rules: [
      "چهار نفر، دو تیم روبه‌رو؛ به هر کس سیزده برگ می‌رسد.",
      "اول مزایده است: نوبتی بید می‌زنی، یعنی سطحی از یک تا هفت با یک خال یا «بی‌حکم»، یا پاس و دوبل می‌گویی.",
      "بالاترین بید «قرارداد» می‌شود و خالش حکم است.",
      "دست یار اعلام‌کننده روی میز باز می‌شود و خودش هر دو دست را بازی می‌کند.",
      "باید شش به‌اضافه‌ی سطح قرارداد دست بگیری؛ اگر نرسی، جریمه به حریف می‌رسد.",
    ],
  },
  // ─── تخته‌ای — فعلاً فقط در اپ (با ربات). ترتیب و متن‌ها همان GameCatalog.Categories[1]. ───
  {
    id: "mench",
    fa: "منچ",
    en: "Mench",
    note: "۲ تا ۴ نفره",
    blurb: "مهره‌ها را به خانه برسان",
    color: "linear-gradient(180deg,#d9574b,#a33a31)",
    pip: "dice",
    online: true,
    rooms: true,
    cat: "board",
    // برچسب‌های قانون‌ها واژه‌به‌واژه همان ui/menchRules.ts و RulesText ِ اپ‌اند (این فایل
    // بی‌ایمپورت می‌مانَد: site/build.ts مستقیم با node می‌خواندش)
    rules: [
      "دو تا چهار نفر؛ هر کس چهار مهره در خانه‌ی رنگ خودش دارد و آن‌ها را دور تخته و از راهروی رنگی خودش به مرکز می‌برد.",
      "مهره با ۶ بیرون می‌آید و به اندازه‌ی تاس جلو می‌رود؛ ۶ یک تاس دیگر می‌دهد و سه ۶ پشت سر هم نوبت را می‌سوزاند. «شروع» را میزبان انتخاب می‌کند: «فقط با ۶»، «سه شانس» (وقتی مهره‌ای بیرون نداری سه بار تاس می‌ریزی تا شش بیاید) یا «با ۱ یا ۶» (شروع بازی خیلی سریع‌تر می‌شود).",
      "اگر روی مهره‌ی حریف بنشینی، به خانه‌اش برمی‌گردد. تا مهره‌ای روی خانه‌ی شروع رنگی ایستاده، آن خانه برای خود او امن است. نقطه‌ی کم‌رنگ نشان می‌دهد هر مهره کجا می‌رود و چه می‌شود: زدن، رسیدن یا خطر.",
      "قانون‌های دلخواه میزبان: «خانه‌ی امن» (روی ستاره‌ها و خانه‌های شروع کسی زده نمی‌شود)، «دیوار» (دو مهره‌ی خودت روی یک خانه: زده نمی‌شوند و کسی از رویشان رد نمی‌شود)، «تاس جایزه» (زدن مهره یا رساندنش به خانه یک تاس دیگر می‌دهد)، «تیمی ۲ به ۲» (یارت روبه‌روست و مهره‌هایت که رسید، برای او تاس می‌ریزی؛ فقط وقتی چهار نفر بنشینند)، «سریع (۲ مهره)» و «تا نفر آخر» (بعد از نفر اول ادامه دارد تا همه‌ی رتبه‌ها معلوم شود).",
      "هر کس زودتر همه‌ی مهره‌هایش را به مرکز برساند، برنده است؛ در «تیمی»، تیمی که مهره‌های هر دو یارش زودتر برسند.",
    ],
  },
  {
    id: "nard",
    fa: "تخته‌نرد",
    en: "Backgammon",
    note: "۲ نفره",
    blurb: "زودتر مهره‌ها را بیرون ببر",
    color: "linear-gradient(180deg,#8a5a34,#5e3a1f)",
    pip: "dice",
    online: true,
    rooms: true,
    cat: "board",
    rules: [
      "دو نفره؛ هر کس پانزده مهره دارد و آن‌ها را دور تخته به خانه‌ی خودش می‌برد.",
      "هر نوبت دو تاس؛ با هر تاس یک مهره همان‌قدر جلو می‌رود. «جفت» چهار حرکت است.",
      "روی خانه‌ای که حریف دو مهره در آن دارد نمی‌شود نشست؛ اگر مهره‌ی تک حریف را بزنی، به بار می‌رود و حریف باید اول آن را از بار وارد کند.",
      "وقتی همه‌ی مهره‌هایت در خانه‌ات بودند، بیرونشان می‌بری؛ هر کس زودتر پانزده مهره را بیرون ببرد، برنده است.",
      "حریفی که هیچ مهره‌ای بیرون نبرده «مارس» می‌شود (دو امتیاز)، و اگر مهره‌اش هنوز روی بار یا در خانه‌ی تو باشد «جول» (سه امتیاز).",
    ],
  },
  {
    id: "marpele",
    fa: "مارپله",
    en: "Snakes & Ladders",
    note: "۲ تا ۴ نفره",
    blurb: "از پله بالا برو، مار نیشت نزند",
    color: "linear-gradient(180deg,#4fa35f,#2f7440)",
    pip: "dice",
    online: true,
    rooms: true,
    cat: "board",
    rules: [
      "دو تا چهار نفر روی تخته‌ی صدخانه‌ای؛ هر بازی تخته و تم تازه‌ای دارد و جای مارها و پله‌ها هر بار عوض می‌شود.",
      "در نوبتت تاس می‌ریزی و مهره‌ات همان‌قدر جلو می‌رود. میزبان می‌تواند دو تاس بگذارد: یکی را خودت انتخاب می‌کنی، یا «سریع» که جمع هر دو است و اگر از صد رد شوی، برمی‌گردی.",
      "اگر پایین پله بیفتی، بالا می‌روی؛ اگر سر مار بیفتی، تا دُمش پایین می‌آیی. حلقه‌ی قرمز سر مارهایی را نشان می‌دهد که تاس این نوبت به آن‌ها می‌رسد.",
      "خانه‌های جادویی چشمک می‌زنند و با هر تاس جا عوض می‌کنند؛ چه از رویشان رد شوی چه رویشان بایستی، اثر می‌کنند: پادزهر (یک نیش را خنثی می‌کند)، شمشیر (سر مار را می‌زند و آن مار تا آخر بازی مرده است)، تاس دوباره، جابه‌جایی با نفر اول، ایست، و ماشین (همین حرکت و سه نوبت بعد، هر مهره‌ای که سر راهت باشد به شروع برمی‌گردد؛ نیش مار ماشین را می‌گیرد).",
      "روی سر مار، اگر پادزهر یا شمشیر داشته باشی، خودت تصمیم می‌گیری. باید دقیقاً روی ۱۰۰ بایستی؛ اولین کسی که برسد، برنده است.",
    ],
  },
  {
    id: "dooz",
    fa: "دوز",
    en: "Four in a row",
    note: "۲ نفره",
    blurb: "با چهارتا پشت هم ببر",
    color: "linear-gradient(180deg,#4a6fb8,#2c4a86)",
    pip: "dice",
    online: true,
    rooms: true,
    cat: "board",
    rules: [
      "دو نفره، روی تخته‌ی هفت ستون و شش ردیف.",
      "نوبتی ستونی را انتخاب می‌کنی؛ مهره‌ات تا پایین‌ترین خانه‌ی خالی همان ستون می‌افتد.",
      "هر کس زودتر چهار مهره‌ی خودش را افقی، عمودی یا مورب پشت هم بچیند، برنده است.",
      "اگر تخته پر شود و کسی چهارتا نچیده باشد، بازی مساوی است.",
    ],
  },
  {
    id: "domino",
    fa: "دومینو",
    en: "Domino",
    note: "۲ تا ۴ نفره",
    blurb: "سرها را جور کن",
    color: "linear-gradient(180deg,#b04a3f,#7c2e27)",
    pip: "dice",
    online: true,
    rooms: true,
    cat: "board",
    rules: [
      "دسته‌ی شش‌شش، ۲۸ مهره؛ دو نفره هر کس ۷ مهره، سه و چهار نفره ۵ مهره.",
      "در نوبتت مهره‌ای می‌گذاری که با یکی از دو سر زنجیره جور باشد.",
      "اگر مهره‌ی جور نداری، از بازار می‌کشی؛ بازار که تمام شد، رد می‌دهی.",
      "هر کس زودتر دستش خالی شود، جمع خال‌های دست بقیه را امتیاز می‌گیرد. اولین کسی که به ۱۰۰ برسد، برنده است.",
    ],
  },
  {
    id: "mafia",
    fa: "مافیا",
    en: "Mafia",
    note: "۵ تا ۱۳ نفره",
    blurb: "مافیا را پیدا کن، یا مافیا باش",
    color: "linear-gradient(180deg,#8a2a3a,#4a1426)",
    pip: "mask",
    online: true,
    rooms: true,
    cat: "party",
    rules: [
      "هر کس یک نقش مخفی دارد: شهروند یا مافیا. مافیاها هم را می‌شناسند، شهروندها نه.",
      "شب مافیا یک نفر را می‌زند و نقش‌ها کارشان را می‌کنند: دکتر نجات می‌دهد، کارآگاه استعلام می‌گیرد.",
      "روز همه حرف می‌زنند؛ رأی اول متهم‌ها را به دفاع می‌برد و رأی خروج یک نفر را بیرون می‌فرستد.",
      "شهروندها با بیرون‌کردن همه‌ی مافیا می‌برند؛ مافیا وقتی شمارش با بقیه برابر شود.",
      "یازده سناریو، از کلاسیک تا پدرخوانده، شب‌های مافیا، زودیاک، تکاور و بازپرس.",
    ],
  },
  {
    // مسابقه‌ی فلش‌ها (docs/arrow-race.md): آفلاین مرحله‌به‌مرحله، آنلاین فقط اتاقِ دوستان. سرور آن را میانِ
    // تخته‌ای‌ها دارد (match.BoardGames، میزِ هم‌زمان)، ولی قفسه‌اش «فکری» است.
    id: "arrows",
    fa: "فلش‌ها",
    en: "Arrows",
    note: "۱ تا ۴ نفره",
    blurb: "فلش‌ها را یکی‌یکی از نقش بیرون بکش",
    color: "linear-gradient(180deg,#4a7bd6,#2f56a8)",
    pip: "arrow",
    online: true,
    rooms: true,
    cat: "puzzle",
    rules: [
      "فلش را بزن تا بیرون برود؛ فقط وقتی می‌رود که سر راهش تا لبه خالی باشد.",
      "آفلاین مرحله‌به‌مرحله است و هر مرحله یک نقش تک‌رنگ. سه قلب داری و فلشی که راهش بسته باشد یک قلب می‌برد؛ ستاره‌ها از قلب‌های مانده است.",
      "آنلاین در اتاق دوستان: میزبان نقش را انتخاب می‌کند و همه با هم روی همان نقش بازی می‌کنند، فلش‌های هر کس یک رنگ.",
      "در مسابقه فلشی که راهش بسته است تو را دو ثانیه یخ می‌زند و اولین کسی که همه‌ی فلش‌هایش را بیرون بکشد می‌برد.",
      "تیمی با چهار نفر دو به دو است و روبه‌روها یارند. هر کس فلش‌هایش تمام شد فلش‌های یارش را هم می‌زند؛ تیمی می‌برد که هر دو نفرش زودتر تمام کنند.",
      "با دوستان میز چند دور است: هر دور نقش تازه‌ای می‌آید و امتیاز هر دور به شمار کسانی است که پشت سرت ماندند؛ بیشترین امتیاز قهرمان است.",
      "در آفلاین سه نقشه‌ی بزرگ هم هست (شیر، اسب و غزال) با پنج قلب، و در همکاری نقشه‌ها یکی در میان جانورند.",
      "در همکاری همه یک تیم‌اند روی نقشه‌ای بزرگ با نه تکه؛ وسط اول باز است و هر تکه که تمام شود تکه‌های کنارش باز می‌شوند. قلب‌ها مشترک است و هر کس فلش‌های رنگش را می‌زند، و وقتی در تکه‌های باز چیزی از رنگش نماند، فلش‌های بقیه را.",
    ],
  },
];

/** آن‌هایی که میزِ آنلاین دارند — تنها چیزی که کلاینت می‌تواند بفرستد. */
export const games = catalog.filter((g) => g.online);

/** بازیِ «فقط اتاقِ دوستان» (تخته‌ایِ آنلاین)؟ صف و ورودی ندارد. */
export const roomsOnly = (id: string): boolean => catalog.some((g) => g.id === id && g.rooms === true);

/**
 * رنگِ پایه‌ی هر بازی برای نورِ پشتِ آرت (lux.css، `--c`). رنگ بالا گرادیان نوشته شده و
 * color-mix گرادیان نمی‌پذیرد؛ رنگِ اولِ همان گرادیان رنگِ خودِ بازی است.
 */
export const gameTint = (c: string): string => c.match(/#[0-9a-f]{6}/i)?.[0] ?? c;

/** آرتِ بازی. نسخه همان ART_V ِ site/build.ts است؛ با تعویضِ تصویرها هر دو با هم بالا می‌روند. */
export const gameArt = (id: string): string => `/art/games/${id}.webp?v=5`;
